import { useForm } from '@tanstack/react-form';
import { useStandortContext } from '../lib/StandortContext';
import { useDatevAuthContext } from '../lib/DatevAuthContext';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@e2n/ui/v2';
import { Input } from '@e2n/ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// oxlint-disable-next-line complexity max-lines-per-function
export function LoginCard() {
  const { t } = useTranslation();
  const { standort } = useStandortContext();
  const { setStatus } = useDatevAuthContext();
  const navigate = useNavigate();

  const lohnabrechnungsart =
    standort?.lohnabrechnungseinstellungen.lohnabrechnungsart;

  const isDatevLohnart =
    lohnabrechnungsart === 'LODAS' || lohnabrechnungsart === 'LOGE';

  const FormSchema = z.object({
    beraternummer: z
      .string()
      .trim()
      .min(1, {
        error: t('domain.datev.login.form.beraternummer.validationError'),
      })
      .max(7, {
        error: t(
          'domain.datev.login.form.beraternummer.validationErrorMaxLength',
        ),
      }),
    mandantennummer: z
      .string()
      .trim()
      .min(1, {
        error: t('domain.datev.login.form.mandantennummer.validationError'),
      })
      .max(5, {
        error: t(
          'domain.datev.login.form.mandantennummer.validationErrorMaxLength',
        ),
      }),
    lohnabrechnungsart: z.enum(['LODAS', 'LOGE']),
  });

  const defaultValues: z.infer<typeof FormSchema> = isDatevLohnart
    ? {
        mandantennummer:
          standort?.lohnabrechnungseinstellungen.mandantennummer ?? '',
        beraternummer:
          standort?.lohnabrechnungseinstellungen.beraternummer ?? '',
        lohnabrechnungsart:
          standort?.lohnabrechnungseinstellungen.lohnabrechnungsart ===
            'LODAS' ||
          standort?.lohnabrechnungseinstellungen.lohnabrechnungsart === 'LOGE'
            ? standort?.lohnabrechnungseinstellungen.lohnabrechnungsart
            : 'LODAS',
      }
    : {
        mandantennummer: '',
        beraternummer: '',
        lohnabrechnungsart: 'LODAS',
      };

  const form = useForm({
    formId: 'datev-login',
    defaultValues,
    validators: {
      onSubmit: FormSchema,
    },
    onSubmit: () => {
      // TODO Login
      setStatus({
        authorized: true,
        expiresAt: dayjs().add(7, 'day').toISOString(),
        name: 'Datev Test',
      });
      navigate('/datev');
    },
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}>
      <Card className="w-96">
        <CardHeader>
          <CardTitle>{t('domain.datev.login.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form.Field name="beraternummer">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t('domain.datev.login.form.beraternummer.label')}
                  </FieldLabel>
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="mandantennummer">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t('domain.datev.login.form.mandantennummer.label')}
                  </FieldLabel>
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="lohnabrechnungsart">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name} aria-invalid={isInvalid}>
                    {t('domain.datev.login.form.lohnsystem.label')}
                  </FieldLabel>
                  <Select
                    value={field.state.value}
                    onValueChange={(val) =>
                      field.handleChange(val as 'LODAS' | 'LOGE')
                    }>
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key={'LODAS'} value="LODAS">
                        {t('domain.datev.login.form.lohnsystem.options.LODAS')}
                      </SelectItem>
                      <SelectItem key={'LOGE'} value="LOGE">
                        {t('domain.datev.login.form.lohnsystem.options.LOGE')}
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
        </CardContent>
        <CardFooter>
          <Button className="w-full" type="submit">
            {t('domain.datev.login.form.submit.label')}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}
